import { ChangeEvent, useEffect, useState } from 'react';
import {
  Flex,
  Button,
  Grid,
  List,
  Text,
  ListItem,
  Box,
} from '@chakra-ui/react';
import { withSiteExtensionData } from '../lib/hoc';
import { useTranslation } from '../lib/hooks.context';
import { mapNewsTeaserProps } from '../lib/resolvers';
import { NewsTeaser, Tag } from '../types';
import SiteSearch from './SiteSearch';
import Teaser from './Teaser';
import TeaserSplit from './TeaserSplit';
import { Select } from './Form/FormField';
import { mhyDateSort } from '../lib/utils';

interface Props {
  blog: {
    items: NewsTeaser[];
    tags: Tag[];
  };
  isLoading?: boolean;
}

const defaultLimit = 20;
const step = defaultLimit;

// State always holds people and filtered is either a subset of that or false
interface State {
  limit: number;
  filter: null | string;
  filteredItems?: null | NewsTeaser[];
}

const filterBlogPosts = (post: NewsTeaser, tag: string) => {
  if (typeof post.tags === 'undefined') {
    return false;
  }
  return post.tags.includes(tag);
};

const BlogList = ({ blog: { items, tags }, isLoading }: Props) => {
  const [{ limit, filter, filteredItems }, setState] = useState<State>({
    limit: defaultLimit,
    filter: null,
    filteredItems: null,
  });
  const t = useTranslation();

  const sortedBlogPosts = [...(filteredItems || items)]
    ?.sort(({ date: a }, { date: b }) => mhyDateSort(a, b))
    .slice(0, limit);

  const showMore = () => {
    setState((s) => ({
      ...s,
      limit: s.limit + step,
    }));
  };

  useEffect(() => {
    // Nice to have mostly. Used to eg. reset the filtering in case some tags are removed (by SiteSearch)
    if (filter && filteredItems) {
      if (!tags?.length || !tags.find(({ slug }) => slug === filter)) {
        // If filtering is on and there's no tags, or filter not in the tags, reset the filter.
        setState((s) => ({
          ...s,
          filter: null,
          filteredItems: null,
        }));
      }
    }
  }, [tags, filteredItems, filter]);

  const onFilter = ({
    target: { value: tag },
  }: ChangeEvent<HTMLSelectElement>) => {
    setState((s) => ({
      ...s,
      filter: tag || null,
      filteredItems: tag
        ? items.filter((post) => filterBlogPosts(post, tag))
        : null,
    }));
  };

  return (
    <Box as="section">
      <Flex
        as="form"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={[8, 10, 14]}
        flexWrap="wrap"
        gridGap="4"
        width="100%"
        sx={{ '> *': { width: 'auto !important' } }}
      >
        <SiteSearch isLoading={isLoading} />
        {tags ? (
          <Select
            onChange={onFilter}
            maxWidth="600px"
            minWidth="175px"
            bg="backgroundColor"
            isDisabled={isLoading}
          >
            <option value="">{t('show_all')}</option>
            {tags.map(({ slug, name }) => (
              <option value={slug} key={slug}>
                {name}
              </option>
            ))}
          </Select>
        ) : null}
      </Flex>
      <Grid
        templateColumns={[
          '1fr',
          'repeat(auto-fit, minmax(250px, 1fr))',
          'repeat(auto-fit, minmax(325px, 1fr))',
        ]}
        gap="10"
        mb="10"
        sx={{ '> *:first-of-type': { gridColumn: '1 / -1' } }}
        as={List}
      >
        {sortedBlogPosts.map((post, i) => {
          const TeaserComponent = i ? Teaser : TeaserSplit;
          return (
            <TeaserComponent
              key={`${post.id || post.title}-${post.site}`}
              as={ListItem}
              {...mapNewsTeaserProps(post, t, !i)}
            />
          );
        })}
        {!items?.length ? <Text>{t('blogs_no_items')}</Text> : null}
      </Grid>
      {items?.length > limit ? (
        <Flex as="footer" justifyContent="center">
          <Button onClick={showMore} variant="solidGreen" size="large">
            {t('load_more')}
          </Button>
        </Flex>
      ) : null}
    </Box>
  );
};

export default withSiteExtensionData<Pick<Props, 'blog'>>(BlogList, {
  param: 'sites',
  dataKey: 'blog',
  dataResolver: (key: 'blog', data: Props['blog'], initial: Props['blog']) => ({
    [key]: {
      items: [...initial.items, ...data.items],
      // Because the tags can be from multiple sites, there might be overlapping. Make sure only unique slugs pass
      tags: [...initial.tags, ...data.tags].reduce<{
        collected: string[];
        tags: Tag[];
      }>(
        (acc, tag) =>
          acc.collected.includes(tag.slug)
            ? acc
            : {
                collected: [...acc.collected, tag.slug],
                tags: [...acc.tags, tag],
              },
        { collected: [], tags: [] },
      ).tags,
    },
  }),
});
